// Settings screen (owner mockup): ornate sidebar-nav shell with General / Graphics / Audio /
// Gameplay / Controls section cards and a staged Apply model.
//
// Values are drafted locally and only committed on Apply (persist + notify the app so an
// in-progress match picks up the live-applicable ones). Back discards the draft.
//
// Wired settings: camera/HUD/gameplay controls, Fullscreen API display mode, real render quality
// profiles + exposure, accessibility effects, and the Web Audio music/effects mix.

import { useState } from 'react';
import {
  DEFAULT_SETTINGS,
  DISPLAY_MODES,
  QUALITY_PRESETS,
  loadSettings,
  saveSettings,
  type DisplayMode,
  type SettingsData,
} from '../../app/persistence';
import menuBg from '../assets/menu-bg.png';
import { Select } from '../components/Select';

type Section = 'general' | 'graphics' | 'audio' | 'gameplay' | 'controls' | 'devtools';

const DISPLAY_LABELS: Record<DisplayMode, string> = {
  fullscreen: 'Fullscreen',
  borderless: 'Borderless Window',
  windowed: 'Windowed',
};

// ---------------------------------------------------------------- reusable controls

function Slider({
  label,
  min,
  max,
  step,
  value,
  format,
  onChange,
}: {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  format: (v: number) => string;
  onChange: (v: number) => void;
}) {
  const pct = `${((value - min) / (max - min)) * 100}%`;
  return (
    <div className="setrow">
      <span className="setlabel">{label}</span>
      <input
        className="setslider"
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        style={{ ['--pct' as string]: pct }}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <span className="setval">{format(value)}</span>
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="settoggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="box" />
      <span className="settoggletext">{label}</span>
    </label>
  );
}

function Dropdown({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="setrow">
      <span className="setlabel">{label}</span>
      <div className="setselect">
        <Select
          ariaLabel={label}
          value={value}
          options={options.map((o) => ({ value: o.value, label: o.label }))}
          onChange={onChange}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- icons

function Sigil() {
  return (
    <svg viewBox="0 0 40 40" width="34" height="34" fill="none" stroke="currentColor" strokeWidth="1.4">
      <path d="M20 3l7 8-7 26-7-26z" strokeLinejoin="round" />
      <path d="M13 11h14M20 3v34" strokeOpacity="0.7" />
      <circle cx="20" cy="15" r="2.4" fill="currentColor" stroke="none" />
    </svg>
  );
}
function IconGear() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <circle cx="12" cy="12" r="3.4" />
      <path d="M12 3.5v2M12 18.5v2M4.2 7l1.7 1M18.1 16l1.7 1M4.2 17l1.7-1M18.1 8l1.7-1M3.5 12h2M18.5 12h2" />
    </svg>
  );
}
function IconMonitor() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
      <rect x="3" y="4.5" width="18" height="12" rx="1.5" />
      <path d="M9 20h6M12 16.5V20" strokeLinecap="round" />
    </svg>
  );
}
function IconSpeaker() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
      <path d="M4 9.5v5h3l5 4V5.5l-5 4z" />
      <path d="M16 9.5a4 4 0 010 5M18.5 7a7 7 0 010 10" strokeLinecap="round" />
    </svg>
  );
}
function IconGamepad() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 8h10a4 4 0 013.9 3.1l.8 4A2.4 2.4 0 0119.4 18c-1 0-1.6-.6-2.2-1.3L16 15H8l-1.2 1.7C6.2 17.4 5.6 18 4.6 18a2.4 2.4 0 01-2.3-2.9l.8-4A4 4 0 017 8z" />
      <path d="M7 12h2M8 11v2M15.5 11.5h.01M17.5 13h.01" />
    </svg>
  );
}
function IconKeyboard() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
      <rect x="2.5" y="6" width="19" height="12" rx="1.5" />
      <path d="M6 9.5h.01M9 9.5h.01M12 9.5h.01M15 9.5h.01M18 9.5h.01M6 12.5h.01M9 12.5h.01M12 12.5h.01M15 12.5h.01M18 12.5h.01M8 15.5h8" strokeLinecap="round" />
    </svg>
  );
}
function IconTools() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 6.5a4 4 0 01-5 5L4 17l3 3 5.5-5.5a4 4 0 005-5l-2.2 2.2-3-3z" />
      <path d="M15.5 4.5 20 9" />
    </svg>
  );
}

const NAV: { id: Section; label: string; icon: () => JSX.Element }[] = [
  { id: 'general', label: 'General', icon: IconGear },
  { id: 'graphics', label: 'Graphics', icon: IconMonitor },
  { id: 'audio', label: 'Audio', icon: IconSpeaker },
  { id: 'gameplay', label: 'Gameplay', icon: IconGamepad },
  { id: 'controls', label: 'Controls', icon: IconKeyboard },
  { id: 'devtools', label: 'Dev Tools', icon: IconTools },
];

// ---------------------------------------------------------------- display mode side effect

function applyDisplayMode(mode: DisplayMode): void {
  try {
    if (mode === 'windowed') {
      if (document.fullscreenElement) void document.exitFullscreen();
    } else if (!document.fullscreenElement) {
      void document.documentElement.requestFullscreen?.();
    }
  } catch {
    // fullscreen may be blocked (no user gesture / permissions) — persisted value still saved
  }
}

// ---------------------------------------------------------------- screen

export function SettingsScreen({
  onBack,
  onChanged,
  onOpenModelViewer,
}: {
  onBack: () => void;
  /** notify the app so an in-progress match can pick up live-applicable values */
  onChanged?: (s: SettingsData) => void;
  /** available from the out-of-match settings hub */
  onOpenModelViewer?: () => void;
}) {
  const [saved, setSaved] = useState<SettingsData>(() => loadSettings());
  const [draft, setDraft] = useState<SettingsData>(saved);
  // one category visible at a time — nav swaps the detail panel instead of scrolling a shared grid
  const [active, setActive] = useState<Section>('general');

  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const set = (patch: Partial<SettingsData>) => setDraft((d) => ({ ...d, ...patch }));

  const apply = () => {
    saveSettings(draft);
    setSaved(draft);
    onChanged?.(draft);
    if (draft.displayMode !== saved.displayMode) applyDisplayMode(draft.displayMode);
  };
  const restore = () => setDraft({ ...DEFAULT_SETTINGS });

  return (
    <div
      className="settingsscreen screen-in"
      style={{ background: `#0a0c12 url(${menuBg}) center / cover no-repeat` }}
    >
      <div className="settingsshade" />
      <header className="settingshead">
        <div className="brandmark">
          <Sigil />
          <span className="brandword">SIGILFRONT</span>
        </div>
        <h1 className="settingstitle">Settings</h1>
      </header>

      <div className="settingsbody">
        <nav className="settingsnav">
          {NAV.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              className={`navitem${active === id ? ' active' : ''}`}
              onClick={() => setActive(id)}
            >
              <Icon />
              <span>{label}</span>
            </button>
          ))}
        </nav>

        <div className="settingscards">
          {/* General */}
          {active === 'general' && (
          <section className="setcard">
            <div className="setcardhead">
              <IconGear />
              <h2>General</h2>
            </div>
            <Slider
              label="Camera Pan Speed"
              min={0.4}
              max={2.5}
              step={0.1}
              value={draft.panSpeed}
              format={(v) => `${v.toFixed(1)}x`}
              onChange={(v) => set({ panSpeed: v })}
            />
            <Slider
              label="Camera Smoothing"
              min={0}
              max={1}
              step={0.05}
              value={draft.cameraSmoothing}
              format={(v) => (v === 0 ? 'Instant' : `${Math.round(v * 100)}%`)}
              onChange={(v) => set({ cameraSmoothing: v })}
            />
            <Slider
              label="HUD Scale"
              min={0.7}
              max={1.4}
              step={0.05}
              value={draft.hudScale}
              format={(v) => `${Math.round(v * 100)}%`}
              onChange={(v) => set({ hudScale: v })}
            />
            <Toggle
              label="Show FPS Counter"
              checked={draft.showFps}
              onChange={(v) => set({ showFps: v })}
            />
            <Toggle
              label="Invert Zoom Direction"
              checked={draft.zoomInvert}
              onChange={(v) => set({ zoomInvert: v })}
            />
          </section>
          )}

          {/* Graphics */}
          {active === 'graphics' && (
          <section className="setcard">
            <div className="setcardhead">
              <IconMonitor />
              <h2>Graphics</h2>
            </div>
            <Dropdown
              label="Display Mode"
              value={draft.displayMode}
              options={DISPLAY_MODES.map((m) => ({ value: m, label: DISPLAY_LABELS[m] }))}
              onChange={(v) => set({ displayMode: v as DisplayMode })}
            />
            <Dropdown
              label="Quality Preset"
              value={draft.quality}
              options={QUALITY_PRESETS.map((q) => ({
                value: q,
                label: q[0].toUpperCase() + q.slice(1),
              }))}
              onChange={(v) => set({ quality: v as SettingsData['quality'] })}
            />
            <p className="setdescription">
              Presets now scale real-time shadows, texture filtering, material reflections, and battlefield effect density.
            </p>
            <Slider
              label="Render Scale"
              min={0.5}
              max={1}
              step={0.05}
              value={draft.renderScale}
              format={(v) => `${Math.round(v * 100)}%`}
              onChange={(v) => set({ renderScale: v })}
            />
            <Slider
              label="Scene Exposure"
              min={0.65}
              max={1.15}
              step={0.05}
              value={draft.sceneExposure}
              format={(v) => `${Math.round(v * 100)}%`}
              onChange={(v) => set({ sceneExposure: v })}
            />
            <p className="setdescription">
              Lower exposure restores shaded faces and keeps bright spell effects from washing out the battlefield.
            </p>
            <Toggle label="Reduced Motion & Flashes" checked={draft.reduceMotion} onChange={(v) => set({ reduceMotion: v })} />
            <Toggle label="Card Foil Effects" checked={draft.foilEffects} onChange={(v) => set({ foilEffects: v })} />
          </section>
          )}

          {/* Audio */}
          {active === 'audio' && (
          <section className="setcard">
            <div className="setcardhead">
              <IconSpeaker />
              <h2>Audio</h2>
            </div>
            <Slider
              label="Master Volume"
              min={0}
              max={100}
              step={1}
              value={draft.masterVol}
              format={(v) => `${v}%`}
              onChange={(v) => set({ masterVol: v })}
            />
            <Slider
              label="Music Volume"
              min={0}
              max={100}
              step={1}
              value={draft.musicVol}
              format={(v) => `${v}%`}
              onChange={(v) => set({ musicVol: v })}
            />
            <p className="setdescription">Controls menu, battle, and base-under-attack music independently from sound effects.</p>
            <Slider
              label="SFX Volume"
              min={0}
              max={100}
              step={1}
              value={draft.sfxVol}
              format={(v) => `${v}%`}
              onChange={(v) => set({ sfxVol: v })}
            />
            <Toggle
              label="Mute Audio When Minimized"
              checked={draft.muteWhenMinimized}
              onChange={(v) => set({ muteWhenMinimized: v })}
            />
          </section>
          )}

          {/* Gameplay */}
          {active === 'gameplay' && (
          <section className="setcard">
            <div className="setcardhead">
              <IconGamepad />
              <h2>Gameplay</h2>
            </div>
            <Toggle
              label="Edge Scrolling"
              checked={draft.edgeScroll}
              onChange={(v) => set({ edgeScroll: v })}
            />
            <Toggle
              label="Quick Cast Card Hotkeys"
              checked={draft.quickCast}
              onChange={(v) => set({ quickCast: v })}
            />
            <Toggle
              label="Show Unit Health Bars"
              checked={draft.healthBars}
              onChange={(v) => set({ healthBars: v })}
            />
            <Toggle
              label="Camera Shake"
              checked={draft.cameraShake}
              onChange={(v) => set({ cameraShake: v })}
            />
            <Toggle
              label="Large Card & Tooltip Text"
              checked={draft.largeCardText}
              onChange={(v) => set({ largeCardText: v })}
            />
            <div className="setdivider">Advanced</div>
            <Toggle
              label="Show UAT Cheat Panel in Matches"
              checked={draft.cheats}
              onChange={(v) => set({ cheats: v })}
            />
            <Toggle
              label="Verbose Alerts"
              checked={draft.alertVerbosity === 'all'}
              onChange={(v) => set({ alertVerbosity: v ? 'all' : 'important' })}
            />
          </section>
          )}

          {/* Controls */}
          {active === 'controls' && (
          <section className="setcard wide">
            <div className="setcardhead">
              <IconKeyboard />
              <h2>Controls</h2>
            </div>
            <div className="controlsgrid">
              <div>
                <span className="ctrlkeys">1–9 0</span> Play deck cards (left to right)
              </div>
              <div>
                <span className="ctrlkeys">Arrows / Edge</span> Pan camera
              </div>
              <div>
                <span className="ctrlkeys">Wheel</span> Zoom
              </div>
              <div>
                <span className="ctrlkeys">Left-drag</span> Box-select
              </div>
              <div>
                <span className="ctrlkeys">Right-click</span> Context order
              </div>
              <div>
                <span className="ctrlkeys">A C D P R E W</span> Attack / capture / defend / patrol / raid / escort / withdraw
              </div>
              <div>
                <span className="ctrlkeys">H</span> Recall / resume the selected zone&apos;s workers
              </div>
              <div>
                <span className="ctrlkeys">F</span> Focus Core
              </div>
              <div>
                <span className="ctrlkeys">Space</span> Pause
              </div>
              <div>
                <span className="ctrlkeys">Esc</span> Cancel order / selection
              </div>
            </div>
          </section>
          )}

          {/* Developer Tools */}
          {active === 'devtools' && (
          <section className="setcard">
            <div className="setcardhead">
              <IconTools />
              <h2>Developer Tools</h2>
            </div>
            <p className="setdescription">
              Inspect every production squad and building, preview audited imported assets with license metadata, and review online replacement candidates in the Art Lab.
            </p>
            <button className="btn primary wide" onClick={onOpenModelViewer} disabled={!onOpenModelViewer}>
              Open Model Viewer &amp; Art Lab
            </button>
            {!onOpenModelViewer && <span className="setnote">Return to the main menu to open the model viewer without leaving an active match.</span>}
          </section>
          )}
        </div>
      </div>

      <footer className="settingsfoot">
        {dirty && <span className="unsaved">● Unsaved changes</span>}
        <button className="btn" onClick={restore}>
          ↻ Restore Defaults
        </button>
        <button className="btn" onClick={onBack}>
          ← Back
        </button>
        <button className="btn primary" onClick={apply} disabled={!dirty}>
          ✔ Apply
        </button>
      </footer>
    </div>
  );
}
